import {
  COPPER,
  MICROSTRIP,
  EMBEDDED_MICROSTRIP,
  STRIPLINE,
  ASYMMETRIC_STRIPLINE
} from '../../constants/globals'
import { findCommonMhzs } from '../layers';
import { mhzToLabel } from "../unitConversions";
import { isOuterCopper } from '../copper';

import microstrip from './formulas/microstrip';
import embeddedMicrostrip from './formulas/embedded-microstrip';
import stripline from './formulas/stripline';
import asymmetricStripline from './formulas/asymmetric-stripline';

export function buildImpedanceModelings(layers) {
  // need to map first because we want to access to allLayers
  return layers
    .map((layer, index, allLayers) => 
      layer.type === COPPER
      ? buildImpedanceModeling(layer, index, allLayers)
      : null
    )
    .filter(layer => layer !== null);
}

function buildImpedanceModeling(copper, layerIndex, layers) {
  const {
    id,
    key,
    displayName,
    signal,
    transmissionSpacing,
    transmissionWidth,
    thickness
  } = copper;
  const referencePlaneUpIndex = findReferencePlaneIndex(layers, layerIndex, 0);
  const referencePlaneDownIndex = findReferencePlaneIndex(layers, layerIndex);

  const dielectricUpHeight = sumPressedThickness(layers, referencePlaneUpIndex, layerIndex);
  const dielectricDownHeight = sumPressedThickness(layers, layerIndex, referencePlaneDownIndex);

  const dielectricConstants =
    weighedAverageOfDielectricConstants(layers, referencePlaneUpIndex, referencePlaneDownIndex);

  const simulationModel = findSimulationModel(
    [referencePlaneUpIndex, referencePlaneDownIndex],
    [dielectricUpHeight, dielectricDownHeight]
  );

  const impedances = signal ? calculateImpedances(
    simulationModel,
    dielectricConstants,
    thickness,
    transmissionWidth,
    transmissionSpacing,
    // Try to make sure that the dielectric down is where there is reference plane:
    // We want to assume that below the dielectric down, there alway is a refrence plane.
    // So if there is no reference plane down, there will be a reference plane up.
    // So place the this part of layers upside down will allow us to use consistant
    // parameters for various calculations
    referencePlaneDownIndex < 0 ? dielectricDownHeight : dielectricUpHeight,
    referencePlaneDownIndex < 0 ? dielectricUpHeight : dielectricDownHeight
  ) : undefined;

  return {
    copperId: id,
    copperKey: key,
    signal,
    name: displayName,
    simulationModel,
    thickness,
    transmissionWidth,
    transmissionSpacing,
    impedances
  }
}

function findReferencePlaneIndex(layers, start, end) {
  const layersToSearch = start > end
    ? layers.slice().reverse().slice(layers.length - start, layers.length - end) 
    : layers.slice(start + 1, end);

  const referencePlane = layersToSearch.find(layer => layer.type === COPPER && !layer.signal);
  return referencePlane ? layers.findIndex(x => x === referencePlane) : -1;
}

function sumPressedThickness(layers, start, end) {
  const from = start < 0 ? 0 : start + 1;
  const to = end < 0 ? layers.length : end;

  return layers
    .slice(from, to)
    .map(layer => layer.type === COPPER && isOuterCopper(layer, layers) ? 0 : layer.pressedThickness)
    .reduce((acc, cur) => acc + cur, 0);
}

function weighedAverageOfDielectricConstants(layers, start, end) {
  const from = start < 0 ? 0 : start;
  const to = end < 0 ? undefined : end;

  const dielectrics = layers.slice(from, to)
    .filter(layer => layer.type !== COPPER);
  const mhzs = findCommonMhzs(dielectrics)
  const totalThickness = dielectrics
    .reduce((acc, layer) => acc + layer.pressedThickness, 0);
  // convert array of array of dielectric constant of objs
  // e.g, [[{ mhz, value}, { mhz, value }, ...], [{ mhz, value}, { mhz, value }, ...], ... ]
  // to an array of object: [{ [mhz]: value, [mhz]: value, ...}]
  // And the value is weighted value, i.e. dk * (thickness / total_thickness);
  const weightedDielectricConstants = dielectrics
    .map(layer =>
      layer.dk.reduce((obj, dk) => {
        obj[dk.mhz] = dk.value * (layer.pressedThickness / totalThickness);
        return obj;
      }, {})
    );

  const dielectricConstants = mhzs.map(mhz => {
    const sum = weightedDielectricConstants
      .reduce((acc, weightedDk) => acc + +weightedDk[mhz], 0);
    return {
      mhz,
      label: mhzToLabel(mhz),
      value: sum
    };
  });

  return dielectricConstants;
}

function findSimulationModel(refPlaneIndexi, dielectricHeights) {
  const refCount = refPlaneIndexi
    .filter(index => index >= 0)
    .length

  if (refCount === 1) {
    const dielectricCounts = dielectricHeights
      .filter(height => height !== 0)
      .length

    return dielectricCounts === 1
      ? MICROSTRIP
      : EMBEDDED_MICROSTRIP
  }

  const dielectricHeight1 = dielectricHeights[0] || 0;
  const dielectricHeight2 = dielectricHeights[1] || 0;

  const heightDifference = Math.abs(dielectricHeight1 - dielectricHeight2);
  const avgHeight = (dielectricHeight1 + dielectricHeight2) / 2;
  const difference = avgHeight !== 0 ? heightDifference / avgHeight : 1;

  return difference > 0.25 ? ASYMMETRIC_STRIPLINE : STRIPLINE;
}

function calculateImpedances(
  simulationModel,
  dielectricConstants,
  thickness,
  transmissionWidth,
  transmissionSpacing,
  dielectricUpHeight,
  dielectricDownHeight
) {
  const impedances = dielectricConstants.map(dk => {
    const { value: dkValue, mhz, label: frequency } = dk;
    const params = {
      thickness,
      transmissionWidth,
      transmissionSpacing,
      dielectricUpHeight,
      dielectricDownHeight,
      dk: dkValue,
    }
    const characteristicImpedance = calculateCharacteristicImpedance(simulationModel, params);
    const differentialImpedance = calculateDifferentialImpedance(simulationModel, params);
    const propagationalDelay = calculatePropagationDelay(simulationModel, params);

    return {
      frequency,
      mhz,
      params,
      characteristicImpedance,
      differentialImpedance,
      propagationalDelay
    }
  });

  return impedances;
}

function calculateCharacteristicImpedance(simulationModel, params) {
  switch (simulationModel) {
    case MICROSTRIP:
      return microstrip.characteristicImpedance(params);
    case EMBEDDED_MICROSTRIP:
      return embeddedMicrostrip.characteristicImpedance(params);
    case STRIPLINE:
      return stripline.characteristicImpedance(params);
    case ASYMMETRIC_STRIPLINE:
      return asymmetricStripline.characteristicImpedance(params);
    default:
      return undefined;
  }
}

function calculateDifferentialImpedance(simulationModel, params) {
  switch (simulationModel) {
    case MICROSTRIP:
      return microstrip.differentialImpedance(params);
    case EMBEDDED_MICROSTRIP:
      return embeddedMicrostrip.differentialImpedance(params);
    case STRIPLINE:
      return stripline.differentialImpedance(params);
    case ASYMMETRIC_STRIPLINE:
      return asymmetricStripline.differentialImpedance(params);
    default:
      return undefined;
  }
}

function calculatePropagationDelay(simulationModel, params) {
  switch (simulationModel) {
    case MICROSTRIP:
      return microstrip.propagationalDelay(params);
    case EMBEDDED_MICROSTRIP:
      return embeddedMicrostrip.propagationalDelay(params);
    case STRIPLINE:
      return stripline.propagationalDelay(params);
    case ASYMMETRIC_STRIPLINE:
      return asymmetricStripline.propagationalDelay(params);
    default:
      return undefined;
  }
}



export default buildImpedanceModelings;


// WEBPACK FOOTER //
// ./src/lib/simulations/impedance.js